import { getClaims } from '@workos-inc/authkit-react';
import { logger } from './logger';

/**
 * @desc First-party mirror of the AuthKit access token, read by the platform deployment's Vercel
 * edge middleware and by nothing else in the app (design D9).
 *
 * AuthKit keeps the access token in JS memory and its own session cookie is scoped to the auth API
 * hostname, so the edge — which sees only what the browser sends to the portal domain — has no
 * other way to know who is asking. The cookie is client-writable by construction, which is why the
 * middleware trusts only its JWKS-verified signature and treats the whole check as
 * defence-in-depth: the backend's permission gates remain the real boundary.
 */
export const TOKEN_COOKIE_NAME = 'h2gc_at';

/** @desc Not `Strict`: a Lax cookie survives the top-level navigation back from AuthKit's UI. */
const baseAttributes = 'Path=/; SameSite=Lax';

/** @desc `Secure` is omitted on plain http so the cookie still works on a local dev server. */
const secure = () => (window.location.protocol === 'https:' ? '; Secure' : '');

/**
 * @desc Mirror the current access token into the cookie, expiring it with the token itself so a
 * stale mirror can never outlive the session it describes. A token whose `exp` has already passed
 * (or which will not decode) clears the cookie rather than writing a useless one.
 */
export function writeTokenCookie(accessToken: string): void {
  let maxAgeSeconds: number;

  try {
    const { exp } = getClaims(accessToken);
    maxAgeSeconds = Math.floor(exp - Date.now() / 1000);
  } catch (error) {
    logger.warn('could not decode access token for the edge cookie', { error });
    clearTokenCookie();
    return;
  }

  if (maxAgeSeconds <= 0) {
    clearTokenCookie();
    return;
  }

  document.cookie = `${TOKEN_COOKIE_NAME}=${accessToken}; ${baseAttributes}; Max-Age=${maxAgeSeconds}${secure()}`;
}

/** @desc Drop the mirror — on sign-out, and whenever a refresh fails terminally. */
export function clearTokenCookie(): void {
  document.cookie = `${TOKEN_COOKIE_NAME}=; ${baseAttributes}; Max-Age=0${secure()}`;
}
